import React, { useState, useEffect, useRef } from 'react';
import { Level, LevelType, LevelFormData, WordSearchGrid } from '../types/Level';
import { Plus, Wand2, Upload, X, Eye, Download, Edit2, Check } from 'lucide-react';
import { renameImage } from '../services/storageService';
import { processAndUploadImage, pastedImageFile, ImageUploadError } from '../utils/imageUpload';
import { generateKeyLetters as buildKeyLetters } from '../studio/keyLetters';
import WordSearchGridEditor from './WordSearchGridEditor';
import { generateAIResult, AIProvider, describeAIError } from '../ai';
import ProviderBadge from './ProviderBadge';

interface LevelFormProps {
  onSubmit: (levelData: Omit<Level, 'id'>, levelNumber: string, originalId?: string) => void;
  initialData?: Level;
  existingLevels: Level[];
  onCancel?: () => void;
  suggestedWords?: string;
}

const LevelForm: React.FC<LevelFormProps> = ({ onSubmit, initialData, existingLevels, onCancel, suggestedWords }) => {
  // Calculate next available level number for new levels
  const getNextLevelNumber = () => {
    if (initialData) return initialData.id; // Editing existing level

    if (existingLevels.length === 0) return '1'; // First level

    // Find the highest numeric ID and add 1
    const numericIds = existingLevels
      .map(level => parseInt(level.id))
      .filter(id => !isNaN(id))
      .sort((a, b) => a - b);

    if (numericIds.length === 0) return '1';

    return (Math.max(...numericIds) + 1).toString();
  };

  // Get a random level type for new levels
  const getRandomLevelType = (): LevelType => {
    const levelTypes = Object.values(LevelType);
    const randomIndex = Math.floor(Math.random() * levelTypes.length);
    return levelTypes[randomIndex];
  };

  const [formData, setFormData] = useState<LevelFormData>({
    levelNumber: getNextLevelNumber(),
    levelType: initialData?.levelType || LevelType.WhatsInThePicture,
    words: initialData?.words || [],
    keyLetters: initialData?.keyLetters || [],
    category: initialData?.category || '',
    difficulty: initialData?.difficulty || 1,
    sentence: initialData?.sentence || '',
    locationName: initialData?.locationName || '',
    image: initialData?.image || '',
    imageSize: initialData?.imageSize,
    wordSearchGrid: initialData?.wordSearchGrid,
    funFact: initialData?.funFact || ''
  });

  // Update form data when initialData changes (for navigation between levels)
  useEffect(() => {
    if (initialData) {
      setFormData({
        levelNumber: initialData.id,
        levelType: initialData.levelType,
        words: initialData.words || [],
        keyLetters: initialData.keyLetters || [],
        category: initialData.category || '',
        difficulty: initialData.difficulty,
        sentence: initialData.sentence || '',
        locationName: initialData?.locationName || '',
        image: initialData.image || '',
        imageSize: initialData.imageSize,
        wordSearchGrid: initialData.wordSearchGrid,
        funFact: initialData.funFact || ''
      });
      // Clear any generation feedback when switching levels
      setGenerationFeedback(null);
    }
  }, [initialData]);

  const [newWord, setNewWord] = useState('');
  const [newKeyLetter, setNewKeyLetter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [generationFeedback, setGenerationFeedback] = useState<string | null>(null);
  const [isRenamingImage, setIsRenamingImage] = useState(false);
  const [newImageName, setNewImageName] = useState('');
  const [autoReduceImage, setAutoReduceImage] = useState(true);
  const [isGeneratingFunFact, setIsGeneratingFunFact] = useState(false);
  const [funFactProvider, setFunFactProvider] = useState<AIProvider | undefined>(undefined);
  const [funFactError, setFunFactError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const levelData: Omit<Level, 'id'> = {
      levelType: formData.levelType,
      difficulty: formData.difficulty,
      funFact: formData.funFact || undefined
    };

    // Only include fields relevant to the level type
    switch (formData.levelType) {
      case LevelType.CategoryWords:
        if (formData.words.length > 0) levelData.words = formData.words;
        if (formData.keyLetters.length > 0) levelData.keyLetters = formData.keyLetters;
        if (formData.category) levelData.category = formData.category;
        break;

      case LevelType.KnownSentence:
      case LevelType.WhatsInThePicture:
      case LevelType.WhatsTheSong:
        if (formData.sentence) levelData.sentence = formData.sentence;
        if (formData.keyLetters.length > 0) levelData.keyLetters = formData.keyLetters;
        if (formData.image) {
          levelData.image = formData.image;
          if (formData.imageSize) levelData.imageSize = formData.imageSize;
        }
        break;

      case LevelType.WhereIsThisLocated:
        if (formData.sentence) levelData.sentence = formData.sentence;
        if (formData.locationName) levelData.locationName = formData.locationName;
        if (formData.keyLetters.length > 0) levelData.keyLetters = formData.keyLetters;
        if (formData.image) {
          levelData.image = formData.image;
          if (formData.imageSize) levelData.imageSize = formData.imageSize;
        }
        break;

      case LevelType.WordSearch:
        // New grid-based approach
        if (formData.wordSearchGrid) {
          levelData.wordSearchGrid = formData.wordSearchGrid;
        }
        // Keep old words array for backward compatibility (migration)
        if (formData.words && formData.words.length > 0) {
          levelData.words = formData.words;
        }
        if (formData.category) levelData.category = formData.category;
        break;
    }

    onSubmit(levelData, formData.levelNumber, initialData?.id);
  };

  const addWord = () => {
    const trimmedWord = newWord.trim();
    if (trimmedWord && !formData.words.includes(trimmedWord)) {
      setFormData(prev => ({
        ...prev,
        words: [...prev.words, trimmedWord]
      }));
      setNewWord('');
    }
  };

  const removeWord = (index: number) => {
    setFormData(prev => ({
      ...prev,
      words: prev.words.filter((_, i) => i !== index)
    }));
  };

  const addKeyLetter = () => {
    const trimmedLetter = newKeyLetter.trim().toUpperCase();
    if (trimmedLetter &&
      !formData.keyLetters.includes(trimmedLetter) &&
      formData.keyLetters.length < 16) {
      setFormData(prev => ({
        ...prev,
        keyLetters: [...prev.keyLetters, trimmedLetter]
      }));
      setNewKeyLetter('');
      setGenerationFeedback(null); // Clear feedback when manually adding letters
    }
  };

  const removeKeyLetter = (index: number) => {
    setFormData(prev => ({
      ...prev,
      keyLetters: prev.keyLetters.filter((_, i) => i !== index)
    }));
    setGenerationFeedback(null); // Clear feedback when manually removing letters
  };

  const generateKeyLetters = () => {
    let sourceText = '';

    // Get text based on level type
    switch (formData.levelType) {
      case LevelType.CategoryWords:
        sourceText = formData.words.join('');
        break;
      case LevelType.KnownSentence:
      case LevelType.WhatsInThePicture:
      case LevelType.WhereIsThisLocated:
      case LevelType.WhatsTheSong:
        sourceText = formData.sentence;
        break;
      case LevelType.WordSearch:
        return; // WordSearch doesn't use key letters
      default:
        return;
    }

    const result = buildKeyLetters(sourceText);
    const originalValidLettersCount = result.sourceCount;
    const randomLettersAdded = result.randomAdded;
    const wasLimitReached = result.truncated;
    const finalLetters = result.letters;

    // Generate feedback message
    let feedback = '';
    if (wasLimitReached) {
      feedback = `⚠️ Found ${originalValidLettersCount} unique letters from source text, but only first 16 were used due to limit.`;
    } else {
      feedback = `✅ All ${originalValidLettersCount} unique letters from source text were included.`;
      if (randomLettersAdded > 0) {
        feedback += ` Added ${randomLettersAdded} random Hebrew letters to reach 16 total.`;
      }
    }

    setGenerationFeedback(feedback);
    setFormData(prev => ({
      ...prev,
      keyLetters: finalLetters
    }));
  };

  const handleGenerateFunFact = async () => {
    let context = '';
    if (formData.levelType === LevelType.CategoryWords || formData.levelType === LevelType.WordSearch) {
       if (!formData.category && (!formData.words || formData.words.length === 0)) {
         alert("Please enter a category or words first.");
         return;
       }
       context = `Category: ${formData.category || 'N/A'}. Words: ${(formData.words || []).join(', ')}`;
    } else if (formData.levelType === LevelType.WhereIsThisLocated) {
       if (!formData.sentence || !formData.locationName) {
         alert("Please enter the city and the exact location first.");
         return;
       }
       context = `City: ${formData.sentence}. Exact Location: ${formData.locationName}`;
    } else {
       if (!formData.sentence) {
         alert("Please enter a sentence first.");
         return;
       }
       context = `Answer: ${formData.sentence}`;
    }

    setIsGeneratingFunFact(true);
    setFunFactError(null);
    const prompt = `Write a short, incredibly interesting, and slightly edgy or mind-blowing fun fact in Hebrew related to the following context: "${context}". The fact MUST be 100% true and verified. Make it surprising or something most people don't know, rather than a generic encyclopedic fact. The fun fact should be 1-2 sentences. Do not include any intro, outro, quotation marks, or emojis. Just the fact itself.`;
    
    try {
      const result = await generateAIResult(prompt);
      setFormData(prev => ({ ...prev, funFact: result.text.trim() }));
      setFunFactProvider(result.provider);
    } catch (e) {
      console.error(e);
      setFunFactError(describeAIError(e));
    } finally {
      setIsGeneratingFunFact(false);
    }
  };

  const uploadImageFile = async (file: File | Blob, filename: string) => {
    try {
      setUploading(true);
      setUploadError(null);
      const { filename: storedName, size } = await processAndUploadImage(file, filename, { autoReduce: autoReduceImage });
      setFormData(prev => ({
        ...prev,
        image: storedName,
        imageSize: size
      }));
    } catch (error) {
      setUploadError(error instanceof ImageUploadError ? error.message : 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadImageFile(file, file.name);
  };

  const levelTypeHasImage =
    formData.levelType !== LevelType.CategoryWords && formData.levelType !== LevelType.WordSearch;

  // Keep the latest upload function reachable from the document-level paste listener
  const uploadImageFileRef = useRef(uploadImageFile);
  uploadImageFileRef.current = uploadImageFile;

  useEffect(() => {
    if (!levelTypeHasImage) return;

    const handlePaste = (event: ClipboardEvent) => {
      const file = event.clipboardData ? pastedImageFile(event.clipboardData.items) : null;
      if (!file) return; // text and other pastes are left alone

      event.preventDefault();
      const extension = file.type.split('/')[1] || 'png';
      uploadImageFileRef.current(file, `pasted_${Date.now()}.${extension}`);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [levelTypeHasImage]);

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: '',
      imageSize: undefined
    }));
    setUploadError(null);
  };

  const handleDownloadImage = async () => {
    if (!formData.image) return;

    try {
      const { getStorage, ref, getDownloadURL } = await import('firebase/storage');
      const storage = getStorage();
      const imageRef = ref(storage, formData.image);

      // Get the download URL
      const downloadUrl = await getDownloadURL(imageRef);

      // Fetch the image as a blob
      const response = await fetch(downloadUrl);
      const blob = await response.blob();

      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = formData.image; // Use the filename as download name
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download image:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleStartRename = () => {
    setNewImageName(formData.image);
    setIsRenamingImage(true);
  };

  const handleRenameImage = async () => {
    if (!formData.image || !newImageName.trim()) {
      alert('Please enter a valid filename');
      return;
    }

    if (newImageName.trim() === formData.image) {
      setIsRenamingImage(false);
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      const { filename, size } = await renameImage(formData.image, newImageName.trim());
      setFormData(prev => ({
        ...prev,
        image: filename,
        imageSize: size
      }));
      setIsRenamingImage(false);
      setNewImageName('');
    } catch (error) {
      console.error('Rename error:', error);
      setUploadError('Failed to rename image. Please try again.');
    } finally {
      setUploading(false);
    }
  };


  const getImageUrl = (filename: string) => {
    if (!filename) return '';
    // Construct Firebase Storage URL based on your storage configuration
    return `https://firebasestorage.googleapis.com/v0/b/thetower-2d922.firebasestorage.app/o/${encodeURIComponent(filename)}?alt=media`;
  };

  const renderImageSection = (sectionId: string) => {
    return (
      <div className="form-group">
        <label>Image {initialData ? '' : '(optional)'}</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Current Image Display */}
          {formData.image && (
            <div style={{
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              padding: '1rem',
              backgroundColor: '#f9fafb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                {!isRenamingImage ? (
                  <>
                    <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151' }}>
                      Current Image: {formData.image}
                    </span>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={handleStartRename}
                        className="btn btn-secondary btn-small"
                        style={{ padding: '0.25rem 0.5rem' }}
                        disabled={uploading}
                      >
                        <Edit2 size={14} />
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadImage}
                        className="btn btn-secondary btn-small"
                        style={{ padding: '0.25rem 0.5rem' }}
                      >
                        <Download size={14} />
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="btn btn-danger btn-small"
                        style={{ padding: '0.25rem 0.5rem' }}
                      >
                        <X size={14} />
                        Remove
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1 }}>
                      <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>
                        New name:
                      </span>
                      <input
                        type="text"
                        value={newImageName}
                        onChange={(e) => setNewImageName(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.9rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px'
                        }}
                        placeholder="Enter new filename"
                        disabled={uploading}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={handleRenameImage}
                        className="btn btn-primary btn-small"
                        style={{ padding: '0.25rem 0.5rem' }}
                        disabled={uploading}
                      >
                        <Check size={14} />
                        {uploading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsRenamingImage(false);
                          setNewImageName('');
                          setUploadError(null);
                        }}
                        className="btn btn-secondary btn-small"
                        style={{ padding: '0.25rem 0.5rem' }}
                        disabled={uploading}
                      >
                        <X size={14} />
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div style={{
                maxWidth: '300px',
                maxHeight: '200px',
                overflow: 'hidden',
                borderRadius: '4px',
                border: '1px solid #d1d5db'
              }}>
                <img
                  src={getImageUrl(formData.image)}
                  alt="Level preview"
                  crossOrigin="anonymous"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                  onError={(e) => {
                    // Fallback if image fails to load
                    e.currentTarget.style.display = 'none';
                    const fallbackDiv = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallbackDiv) {
                      fallbackDiv.style.display = 'block';
                    }
                  }}
                />
                <div style={{
                  display: 'none',
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '0.9rem'
                }}>
                  <Eye size={24} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
                  Image preview not available
                </div>
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploading}
              style={{ display: 'none' }}
              id={sectionId}
            />
            <label
              htmlFor={sectionId}
              className="btn btn-secondary"
              style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
            >
              <Upload size={16} />
              {uploading ? 'Uploading...' : (formData.image ? 'Replace Image' : 'Upload Image')}
            </label>
            <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', color: '#64748b' }}>
              or paste an image with Ctrl+V
            </span>

            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-start' }}>
              <input
                type="checkbox"
                id="autoReduceImage"
                checked={autoReduceImage}
                onChange={(e) => setAutoReduceImage(e.target.checked)}
                disabled={uploading}
                style={{ width: '16px', height: '16px', margin: 0, padding: 0, cursor: 'pointer' }}
              />
              <label htmlFor="autoReduceImage" style={{ fontSize: '0.85rem', color: '#64748b', margin: 0, fontWeight: 'normal', cursor: 'pointer' }}>
                Automatically reduce image size (max 200KB)
              </label>
            </div>

            {uploadError && (
              <div style={{ color: '#ef4444', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {uploadError}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  const renderKeyLettersSection = () => (
    <div className="form-group">
      <label>Key Letters ({formData.keyLetters.length}/16)</label>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <button
          type="button"
          onClick={generateKeyLetters}
          className="btn btn-secondary btn-small"
          disabled={!formData.sentence}
        >
          <Wand2 size={16} />
          Generate from Sentence
        </button>
      </div>
      {generationFeedback && (
        <div style={{
          color: generationFeedback.includes('⚠️') ? '#f59e0b' : '#10b981',
          fontSize: '0.9rem',
          marginBottom: '0.5rem',
          padding: '0.5rem',
          backgroundColor: generationFeedback.includes('⚠️') ? '#fef3c7' : '#d1fae5',
          borderRadius: '4px',
          border: `1px solid ${generationFeedback.includes('⚠️') ? '#f59e0b' : '#10b981'}`
        }}>
          {generationFeedback}
        </div>
      )}
      <div className="array-input">
        {formData.keyLetters.map((letter, index) => (
          <div key={index} className="array-item">
            {letter}
            <button type="button" onClick={() => removeKeyLetter(index)}>×</button>
          </div>
        ))}
      </div>
      <div className="add-item-input">
        <input
          type="text"
          value={newKeyLetter}
          onChange={(e) => setNewKeyLetter(e.target.value)}
          placeholder="Add a key letter"
          maxLength={1}
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyLetter())}
          disabled={formData.keyLetters.length >= 16}
        />
        <button
          type="button"
          onClick={addKeyLetter}
          className="btn btn-secondary btn-small"
          disabled={formData.keyLetters.length >= 16}
        >
          <Plus size={16} />
        </button>
      </div>
      {formData.keyLetters.length >= 16 && (
        <small style={{ color: '#ef4444', marginTop: '0.5rem' }}>
          Maximum 16 key letters reached
        </small>
      )}
      {newKeyLetter.trim() && formData.keyLetters.includes(newKeyLetter.trim().toUpperCase()) && (
        <small style={{ color: '#ef4444', marginTop: '0.5rem' }}>
          This letter is already added
        </small>
      )}
    </div>
  );

  const renderFieldsForLevelType = () => {
    switch (formData.levelType) {
      case LevelType.CategoryWords:
        return (
          <>
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Animals, Food, Colors"
                required
              />
            </div>

            {suggestedWords && (
              <div className="form-group" style={{ backgroundColor: '#f0f9ff', padding: '1rem', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                <label style={{ color: '#0369a1', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Wand2 size={16} /> AI Suggested Words
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {suggestedWords.split(',').map(w => w.trim()).filter(w => w.length > 0).map((word, idx) => {
                    const isAdded = formData.words.includes(word);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          if (!isAdded) {
                            setFormData(prev => ({ ...prev, words: [...prev.words, word] }));
                          }
                        }}
                        disabled={isAdded}
                        style={{
                          background: isAdded ? '#e2e8f0' : 'white',
                          border: `1px solid ${isAdded ? '#cbd5e1' : '#3b82f6'}`,
                          color: isAdded ? '#94a3b8' : '#2563eb',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '16px',
                          fontSize: '0.85rem',
                          cursor: isAdded ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s'
                        }}
                      >
                        {word} {isAdded ? <Check size={12} /> : <Plus size={12} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="form-group">
              <label>Words</label>
              <div className="array-input">
                {formData.words.map((word, index) => (
                  <div key={index} className="array-item">
                    {word}
                    <button type="button" onClick={() => removeWord(index)}>×</button>
                  </div>
                ))}
              </div>
              <div className="add-item-input">
                <input
                  type="text"
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  placeholder="Add a word"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addWord())}
                />
                <button type="button" onClick={addWord} className="btn btn-secondary btn-small">
                  <Plus size={16} />
                </button>
              </div>
              {newWord.trim() && formData.words.includes(newWord.trim()) && (
                <small style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                  This word is already added
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Key Letters ({formData.keyLetters.length}/16)</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  onClick={generateKeyLetters}
                  className="btn btn-secondary btn-small"
                  disabled={formData.words.length === 0}
                >
                  <Wand2 size={16} />
                  Generate from Words
                </button>
              </div>
              {generationFeedback && (
                <div style={{
                  color: generationFeedback.includes('⚠️') ? '#f59e0b' : '#10b981',
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: generationFeedback.includes('⚠️') ? '#fef3c7' : '#d1fae5',
                  borderRadius: '4px',
                  border: `1px solid ${generationFeedback.includes('⚠️') ? '#f59e0b' : '#10b981'}`
                }}>
                  {generationFeedback}
                </div>
              )}
              <div className="array-input">
                {formData.keyLetters.map((letter, index) => (
                  <div key={index} className="array-item">
                    {letter}
                    <button type="button" onClick={() => removeKeyLetter(index)}>×</button>
                  </div>
                ))}
              </div>
              <div className="add-item-input">
                <input
                  type="text"
                  value={newKeyLetter}
                  onChange={(e) => setNewKeyLetter(e.target.value)}
                  placeholder="Add a key letter"
                  maxLength={1}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyLetter())}
                  disabled={formData.keyLetters.length >= 16}
                />
                <button
                  type="button"
                  onClick={addKeyLetter}
                  className="btn btn-secondary btn-small"
                  disabled={formData.keyLetters.length >= 16}
                >
                  <Plus size={16} />
                </button>
              </div>
              {formData.keyLetters.length >= 16 && (
                <small style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                  Maximum 16 key letters reached
                </small>
              )}
              {newKeyLetter.trim() && formData.keyLetters.includes(newKeyLetter.trim().toUpperCase()) && (
                <small style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                  This letter is already added
                </small>
              )}
            </div>
          </>
        );

      case LevelType.KnownSentence:
        return (
          <>
            <div className="form-group">
              <label>Sentence</label>
              <textarea
                value={formData.sentence}
                onChange={(e) => setFormData(prev => ({ ...prev, sentence: e.target.value }))}
                placeholder="Enter the sentence to be guessed"
                required
              />
            </div>

            {renderImageSection("image-upload-known-sentence")}

            <div className="form-group">
              <label>Key Letters ({formData.keyLetters.length}/16)</label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <button
                  type="button"
                  onClick={generateKeyLetters}
                  className="btn btn-secondary btn-small"
                  disabled={!formData.sentence}
                >
                  <Wand2 size={16} />
                  Generate from Sentence
                </button>
              </div>
              {generationFeedback && (
                <div style={{
                  color: generationFeedback.includes('⚠️') ? '#f59e0b' : '#10b981',
                  fontSize: '0.9rem',
                  marginBottom: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: generationFeedback.includes('⚠️') ? '#fef3c7' : '#d1fae5',
                  borderRadius: '4px',
                  border: `1px solid ${generationFeedback.includes('⚠️') ? '#f59e0b' : '#10b981'}`
                }}>
                  {generationFeedback}
                </div>
              )}
              <div className="array-input">
                {formData.keyLetters.map((letter, index) => (
                  <div key={index} className="array-item">
                    {letter}
                    <button type="button" onClick={() => removeKeyLetter(index)}>×</button>
                  </div>
                ))}
              </div>
              <div className="add-item-input">
                <input
                  type="text"
                  value={newKeyLetter}
                  onChange={(e) => setNewKeyLetter(e.target.value)}
                  placeholder="Add a key letter"
                  maxLength={1}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyLetter())}
                  disabled={formData.keyLetters.length >= 16}
                />
                <button
                  type="button"
                  onClick={addKeyLetter}
                  className="btn btn-secondary btn-small"
                  disabled={formData.keyLetters.length >= 16}
                >
                  <Plus size={16} />
                </button>
              </div>
              {formData.keyLetters.length >= 16 && (
                <small style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                  Maximum 16 key letters reached
                </small>
              )}
              {newKeyLetter.trim() && formData.keyLetters.includes(newKeyLetter.trim().toUpperCase()) && (
                <small style={{ color: '#ef4444', marginTop: '0.5rem' }}>
                  This letter is already added
                </small>
              )}
            </div>
          </>
        );

      case LevelType.WordSearch:
        return (
          <>
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g., Animals, Food, Colors"
                required
              />
            </div>

            <div className="form-group">
              <label style={{ fontSize: '1.2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                Word Search Grid Editor
              </label>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Create your word search grid by placing words and filling remaining cells with random letters.
                {formData.words && formData.words.length > 0 && !formData.wordSearchGrid && (
                  <strong style={{ display: 'block', marginTop: '0.5rem', color: '#f59e0b' }}>
                    ⚠️ This level uses the old word list format. Please recreate the grid using the editor below.
                  </strong>
                )}
              </p>

              <WordSearchGridEditor
                grid={formData.wordSearchGrid}
                category={formData.category}
                onChange={(grid: WordSearchGrid) => setFormData(prev => ({ ...prev, wordSearchGrid: grid }))}
                suggestedWords={suggestedWords}
              />
            </div>
          </>
        );

      case LevelType.WhereIsThisLocated:
        return (
          <>
            {renderImageSection("image-upload-picture")}

            <div className="form-group">
              <label>City (The Answer)</label>
              <textarea
                value={formData.sentence}
                onChange={(e) => setFormData(prev => ({ ...prev, sentence: e.target.value }))}
                placeholder="Enter the city name where this is located"
                required
                rows={1}
              />
            </div>

            <div className="form-group">
              <label>Actual Location Name (For AI Context only)</label>
              <textarea
                value={formData.locationName}
                onChange={(e) => setFormData(prev => ({ ...prev, locationName: e.target.value }))}
                placeholder="e.g. מזרקת דיזנגוף, טיילת לואי, מגדל דוד"
                rows={1}
              />
              <small style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                This helps the AI suggest facts about this exact place, not just the city. It won't be shown to players during gameplay.
              </small>
            </div>

            {renderKeyLettersSection()}
          </>
        );

      case LevelType.WhatsInThePicture:
      case LevelType.WhatsTheSong:
        return (
          <>
            {renderImageSection("image-upload-picture")}

            <div className="form-group">
              <label>Sentence</label>
              <textarea
                value={formData.sentence}
                onChange={(e) => setFormData(prev => ({ ...prev, sentence: e.target.value }))}
                placeholder="Enter the sentence that describes the image"
                required
              />
            </div>

            {renderKeyLettersSection()}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form-container">
      <div className="form-grid">
        <div className="form-group">
          <label>Level Number</label>
          <input
            type="number"
            value={formData.levelNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, levelNumber: e.target.value }))}
            placeholder="Enter level number"
            min="1"
            required
          />
        </div>

        <div className="form-group">
          <label>Level Type</label>
          <select
            value={formData.levelType}
            onChange={(e) => setFormData(prev => ({ ...prev, levelType: e.target.value as LevelType }))}
            required
          >
            <option value={LevelType.CategoryWords}>Category Words</option>
            <option value={LevelType.KnownSentence}>Known Sentence</option>
            <option value={LevelType.WordSearch}>Word Search</option>
            <option value={LevelType.WhatsInThePicture}>What's in the Picture</option>
            <option value={LevelType.WhereIsThisLocated}>Where is This Located</option>
            <option value={LevelType.WhatsTheSong}>What's the Song</option>
          </select>
        </div>

        <div className="form-group">
          <label>Difficulty (1-5)</label>
          <input
            type="number"
            value={formData.difficulty}
            onChange={(e) => setFormData(prev => ({ ...prev, difficulty: parseInt(e.target.value) }))}
            min="1"
            max="5"
            required
          />
        </div>
      </div>

      {renderFieldsForLevelType()}

      <div className="form-group" style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
            <Edit2 size={18} color="#8b5cf6" />
            Fun Fact (Optional)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ProviderBadge provider={funFactProvider} />
            <button
              type="button"
              onClick={handleGenerateFunFact}
              className="btn btn-secondary btn-small"
              disabled={isGeneratingFunFact}
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
            >
              <Wand2 size={14} />
              {isGeneratingFunFact ? 'Generating...' : 'AI Suggestion'}
            </button>
          </div>
        </div>
        <textarea
          value={formData.funFact}
          onChange={(e) => setFormData(prev => ({ ...prev, funFact: e.target.value }))}
          placeholder="Enter an interesting fact that will show after solving this level..."
          rows={1}
          style={{ direction: 'rtl', textAlign: 'right', minHeight: 'auto' }}
        />
        {funFactError && (
          <div style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span>{funFactError}</span>
            <button type="button" className="btn btn-secondary btn-small" onClick={handleGenerateFunFact} disabled={isGeneratingFunFact}>
              Retry
            </button>
          </div>
        )}
        <small style={{ color: '#6b7280', marginTop: '0.25rem' }}>
          This will appear in a popup after the level is solved and before proceeding.
        </small>
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update Level' : 'Create Level'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default LevelForm;
